<template>
  <div class="advanced-table-wrapper">
    <div class="table-description">
      Sepette indirim kampanya tekliflerine katılarak ana sayfada görünürlük
      elde edebilirsiniz. Kampanyaya dahil olan ürünlerinizin sıralaması,
      performansa göre değişiklik gösterebilir.
    </div>

    <div class="table-area">
      <div class="actions-row">
        <div v-if="campaigns.length > 0" class="sort-wrapper">
          <Dropdown
            :dropdown-state="sortDropdownOpen"
            @dropdown-state="sortDropdownOpen = $event"
            position="bottom-left"
          >
            <template #dropdown-click>
              <button class="sort-button">
                <Icons
                  name="Sort"
                  color="#1C1C1E"
                  size="sm"
                  class="sort-icon"
                />
                <span class="sort-label">{{
                  hasSorted ? sortOptions[sortBy] : 'Sırala'
                }}</span>
                <Icons
                  :name="sortDropdownOpen ? 'TopArrows' : 'BottomArrows'"
                  color="#7382A0"
                  size="sm"
                  class="dropdown-arrow"
                />
              </button>
            </template>
            <template #dropdown-content>
              <ul class="sort-dropdown-content">
                <li v-for="(value, key) in sortOptions" :key="key">
                  <button
                    type="button"
                    @click="handleSortChange(key)"
                    :class="{ active: sortBy === key }"
                  >
                    {{ value }}
                  </button>
                </li>
              </ul>
            </template>
          </Dropdown>
        </div>
        <div v-if="campaigns.length > 0" class="pagination-wrapper">
          <Pagination
            :page-count="totalPages"
            :selected-page-index="currentPage"
            :page-range="5"
            :show-per-page="true"
            :per-page-items="{
              10: '10 Kampanya',
              25: '25 Kampanya',
              50: '50 Kampanya',
            }"
            :selected-per-page-key="pageSize"
            :per-page-menu-position="'top-left'"
            @change-index="handlePageChange"
            @change-per-page="handlePageSizeChange"
          />
        </div>
      </div>
      <div class="table-wrapper">
        <table class="advanced-table">
          <thead>
            <tr>
              <th>Kampanya Adı</th>
              <th>Kampanya Tarihleri</th>
              <th>İşlemler</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="isLoading" class="loading-row">
              <td colspan="3">
                <div class="loading">
                  <img src="@/assets/images/loading.gif" alt="Loading" />
                  <span class="loading-text">Yükleniyor</span>
                </div>
              </td>
            </tr>
            <tr v-else-if="campaigns.length === 0" class="empty-row">
              <td colspan="3">
                <div class="empty-table">
                  <img
                    src="@/assets/images/empty-search.png"
                    alt="Kampanya bulunamadı"
                  />
                  <span class="title">
                    {{
                      activeTab === 'available'
                        ? 'Katılabileceğiniz kampanya bulunmuyor.'
                        : 'Katıldığınız kampanya bulunmuyor.'
                    }}
                  </span>
                </div>
              </td>
            </tr>
            <tr
              v-else
              v-for="campaign in campaigns"
              :key="campaign.id"
              :class="{ 'newly-joined': campaign.isNewlyJoined }"
            >
              <td>
                <div class="campaign-name">
                  <div class="campaign-title">{{ campaign.name }}</div>
                  <div class="campaign-badges">
                    <span
                      v-if="campaign.commissionDiscount"
                      class="commission-badge"
                      >{{ campaign.commissionDiscount }}</span
                    >
                    <span class="commission-badge">Ana Sayfada Görünürlük</span>
                  </div>
                </div>
              </td>
              <td>
                <div class="campaign-dates">
                  <span :class="['status-badge', campaign.statusClass]">{{
                    campaign.status
                  }}</span>
                  <div class="date-range">{{ campaign.dateRange }}</div>
                  <div class="last-participation">
                    <div class="last-participation-label">
                      Son Katılım Tarihi:
                    </div>
                    <div class="last-participation-date">
                      {{ campaign.endDate }}
                    </div>
                  </div>
                </div>
              </td>
              <td>
                <div class="action-buttons">
                  <button
                    class="btn-detail"
                    @click="handleGoToDetail(campaign.id)"
                  >
                    Kampanya Detayına Git
                  </button>
                  <button
                    class="btn-join"
                    @click="handleJoinCampaign(campaign.id)"
                  >
                    {{
                      activeTab === 'available'
                        ? 'Kampanyaya Katıl'
                        : 'Kampanyayı Yönet'
                    }}
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div
        v-if="!isLoading && campaigns.length > 0"
        class="pagination bottom-pagination"
      >
        <Pagination
          :page-count="totalPages"
          :selected-page-index="currentPage"
          :page-range="5"
          :show-per-page="true"
          :per-page-items="{
            10: '10 Kampanya',
            25: '25 Kampanya',
            50: '50 Kampanya',
          }"
          :selected-per-page-key="pageSize"
          :per-page-menu-position="'top-left'"
          @change-index="handlePageChange"
          @change-per-page="handlePageSizeChange"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { Icons, Pagination, Dropdown } from '@n11/so-ui-components';

const props = defineProps({
  campaigns: {
    type: Array,
    default: () => [],
  },
  currentPage: {
    type: Number,
    default: 0,
  },
  totalPages: {
    type: Number,
    default: 0,
  },
  pageSize: {
    type: Number,
    default: 10,
  },
  sortBy: {
    type: String,
    default: '',
  },
  activeTab: {
    type: String,
    default: 'available',
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'page-change',
  'page-size-change',
  'sort-change',
  'go-to-detail',
  'join-campaign',
]);

const sortDropdownOpen = ref(false);
const hasSorted = ref(false);

const sortOptions = {
  COMMISSION_DISCOUNT_DESC: 'Komisyona Göre',
  START_DATE_DESC: 'Başlangıç Tarihi',
  END_DATE_DESC: 'Son Katılım Tarihi',
  CREATE_DATE_DESC: 'Oluşturulma Tarihi',
};

const handlePageChange = (pageIndex) => {
  emit('page-change', pageIndex);
};

const handlePageSizeChange = (newPageSize) => {
  emit('page-size-change', Number(newPageSize));
};

const handleSortChange = (selectedSort) => {
  sortDropdownOpen.value = false;
  hasSorted.value = true;
  emit('sort-change', selectedSort);
};

const handleGoToDetail = (campaignId) => {
  emit('go-to-detail', campaignId);
};

const handleJoinCampaign = (campaignId) => {
  emit('join-campaign', campaignId);
};
</script>

<style scoped lang="scss">
@import '@/assets/styles/variables';

.advanced-table-wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-top: 24px;
  margin-bottom: 100px;
  width: 100%;
  padding: 16px 24px;
  background: $colorN00;
  border-radius: 8px;

  .table-description {
    color: $colorN80;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: -0.05px;
  }

  .table-area {
    position: relative;
    z-index: 0;

    .actions-row {
      display: flex;
      width: 100%;
      justify-content: space-between;
      margin-bottom: 16px;

      .sort-wrapper {
        position: relative;
        z-index: 10;
        display: flex;
        align-items: center;
        gap: 8px;

        .sort-button {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          border: 0.5px solid $colorD30;
          border-radius: 6px;
          background: $colorN00;
          cursor: pointer;
          font-size: 14px;
          color: $colorN80;
          white-space: nowrap;

          .sort-icon,
          .dropdown-arrow {
            flex-shrink: 0;
          }

          .sort-label {
            font-size: 14px;
            font-weight: 400;
            color: $colorN80;
          }
        }

        .sort-dropdown-content {
          position: relative;
          z-index: 11;
          border-radius: 6px;
          background: $colorN00;
          min-width: 200px;
          list-style: none;
          margin: 0;
          border: 1px solid $colorN50;
          box-shadow: 0 0 0 3px rgba(180, 180, 180, 0.3);

          li button {
            width: 100%;
            padding: 8px 12px;
            text-align: left;
            border: none;
            background: transparent;
            cursor: pointer;
            font-size: 14px;
            color: $colorN80;
            font-weight: 400;
            border-bottom: 0.5px solid $colorN30;

            &:hover,
            &.active {
              border-radius: 6px 6px 0 0;
              background: $colorN10;
              font-weight: 700;
            }
          }
        }
      }
    }

    .table-wrapper {
      position: relative;
      overflow: scroll auto;
      width: 100%;
      max-width: 1328px;
      max-height: 90dvh;
      border: 1px solid $colorN50;
      border-radius: 8px;
      border-collapse: separate;
      border-spacing: 0;

      .loading-row,
      .empty-row {
        td {
          padding: 0 !important;
          border: none !important;
        }
      }

      .loading {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 64px 0;
        min-height: 400px;

        img {
          max-width: 120px;
          margin-bottom: 16px;
        }

        .loading-text {
          display: block;
          color: $colorN80;
          font-size: 16px;
          font-weight: 600;
          line-height: 24px;
          text-align: center;
        }
      }

      .empty-table {
        position: sticky;
        left: 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 64px 0;
        min-height: 400px;

        img {
          max-width: 120px;
          margin-bottom: 24px;
        }

        .title {
          display: block;
          color: $colorN80;
          font-size: 16px;
          font-weight: 600;
          line-height: 24px;
          text-align: center;
        }
      }

      .advanced-table {
        width: 100%;
        border-radius: 8px;
        border-collapse: separate;

        thead {
          background: $colorD10;
          color: $colorN80;

          tr {
            height: 50px;

            th {
              position: sticky !important;
              z-index: 2 !important;
              top: 0;
              padding: 14px 8px 14px 12px;
              border-bottom: 2px solid $colorN30;
              background-color: $colorBe30;
              color: $colorN80;
              font-size: 13px;
              font-weight: 600;
              letter-spacing: -0.05px;
              line-height: 18px;
              text-align: left;
              vertical-align: middle;

              &:not(:last-child) {
                border-right: 1px solid $colorN30;
              }
            }
          }
        }

        tbody {
          tr {
            &:not(:last-child) td {
              border-bottom: 2px solid $colorN30;
            }

            &.newly-joined {
              td {
                background-color: #edf9ec;
              }
            }

            td {
              height: 90px;
              background-color: $colorN00;
              vertical-align: middle;
              padding: 14px 8px 14px 12px;
              color: $colorN80;
              font-size: 13px;
              letter-spacing: -0.05px;
              line-height: 18px;

              &:not(:last-child) {
                border-right: 1px solid $colorN30;
              }

              .campaign-name {
                display: flex;
                flex-direction: column;
                gap: 8px;

                .campaign-title {
                  color: $colorN80;
                  font-size: 13px;
                  font-weight: 400;
                  line-height: 18px;
                  letter-spacing: -0.05px;
                  word-break: break-word;
                  width: 631px;
                }

                .campaign-badges {
                  display: flex;
                  flex-wrap: wrap;
                  gap: 6px;
                }

                .commission-badge {
                  display: inline-block;
                  padding: 2px 8px;
                  width: fit-content;
                  border-radius: 4px;
                  border: 1px solid $colorN30;
                  background: $colorN00;
                  color: $colorN60;
                  text-align: center;
                  font-size: 11px;
                  font-weight: 600;
                  line-height: 24px;
                }
              }

              .campaign-dates {
                display: flex;
                flex-direction: column;
                gap: 8px;
                align-items: flex-start;

                .status-badge {
                  display: inline-block;
                  padding: 4px 12px;
                  border-radius: 12px;
                  font-size: 12px;
                  font-weight: 600;
                  line-height: 16px;
                  width: fit-content;

                  &.status-ongoing,
                  &.status-ending,
                  &.status-upcoming {
                    border-radius: 4px;
                    border: 0.5px solid $colorN30;
                    font-size: 11px;
                    font-weight: 600;
                    line-height: 24px;
                  }

                  &.status-ongoing {
                    background: $colorB05;
                    color: $colorB60;
                    font-style: normal;
                  }

                  &.status-ending {
                    background: $colorY05;
                    color: $colorY60;
                    padding: 2px 8px;
                  }

                  &.status-upcoming {
                    background: $colorL50;
                    color: $colorN80;
                    padding: 2px 8px;
                  }
                }

                .date-range {
                  color: $colorN80;
                  font-size: 13px;
                  font-weight: 600;
                  line-height: 18px;
                  letter-spacing: -0.05px;
                }

                .last-participation {
                  display: flex;
                  flex-direction: column;
                  color: $colorN80;
                  font-size: 12px;
                  font-weight: 400;
                  line-height: 16px;
                  gap: 2px;

                  .last-participation-label {
                    color: #6b6b6b;
                    font-size: 11px;
                    font-weight: 400;
                    line-height: 14px;
                  }

                  .last-participation-date {
                    color: $colorN80;
                    font-size: 13px;
                    font-weight: 600;
                    line-height: 18px;
                    letter-spacing: -0.05px;
                  }
                }
              }

              .action-buttons {
                display: flex;
                flex-direction: column;
                gap: 8px;
                align-items: center;

                .btn-detail,
                .btn-join {
                  width: 149px;
                  border-radius: 4px;
                  font-weight: 600;
                  cursor: pointer;
                  white-space: nowrap;
                  font-size: 11px;
                  line-height: 14px;
                }

                .btn-detail {
                  padding: 4px 12px;
                  border: 1px solid $colorN30;
                  background: $colorN00;
                  color: $colorN80;
                  font-style: normal;
                }

                .btn-join {
                  padding: 8px 16px;
                  border: none;
                  background: $colorN80;
                  color: $colorN00;
                }
              }
            }
          }
        }
      }
    }

    .pagination-wrapper {
      :deep(.simpleSelect-btn.simpleSelect-btn) {
        width: 100% !important;
      }
    }
    .pagination {
      display: flex;
      width: 100%;
      align-items: center;
      justify-content: flex-end;
      gap: 24px;
      margin-top: 24px;
    }
  }
}
</style>
